import React, { useState, useEffect } from "react";
import { FaCheckCircle, FaArrowUp, FaTimes } from "react-icons/fa";
import { HiArrowTrendingUp } from "react-icons/hi2";
import "./RoomUpsellPopup.css";
import { useTranslation } from "react-i18next";
import { currency } from "../../utils/currency";
import { getFixedValue } from "../../utils/utils";

const RoomUpsellPopup = ({
  currentRoom = { name: "Standard Room", amenities: [] },
  upgradedRoom = { name: "Classic Room", amenities: [] },
  onUpgradeClick,
  onDismiss,
  priceDifference = 500,
  isOpen = false,
}) => {
  const { t } = useTranslation();
  const [uniqueAmenities, setUniqueAmenities] = useState([]);

  // Calculate unique amenities that are in upgradedRoom but not in currentRoom
  useEffect(() => {
    const calculateUniqueAmenities = () => {
      // Get amenities from both rooms
      const currentAmenities =
        currentRoom.HotelRoomAmenities?.map((a) => a.Amenity?.name) || [];
      const upgradedAmenities =
        upgradedRoom.HotelRoomAmenities?.filter(
          (a) => ![32, 78].includes(a.Amenity?.id)
        ).map((a) => a.Amenity?.name) || [];

      // Filter out amenities that exist in currentRoom
      const unique = upgradedAmenities.filter(
        (amenity) => !currentAmenities.includes(amenity)
      );

      setUniqueAmenities(unique);
    };

    calculateUniqueAmenities();
  }, [currentRoom, upgradedRoom]);

  // Format price difference
  const formattedPriceDiff = priceDifference.toFixed(
    getFixedValue(currentRoom?.currency)
  );

  if (!isOpen) return null;

  return (
    <div className="room-upsell-popup-overlay">
      <div className="room-upsell-popup-container">
        <div className="room-upsell-popup-header">
          <div className="room-upsell-badge">
            <HiArrowTrendingUp />
            <span>{t("Upgrade available")}</span>
          </div>
          <button className="room-upsell-close" onClick={onDismiss}>
            <FaTimes />
          </button>
        </div>

        <div className="room-upsell-popup-content">
          <div className="room-upsell-popup-main">
            <h3>
              {t("Upgrade to")}{" "}
              <span className="highlighted-room-name">
                {upgradedRoom.roomName}
              </span>
            </h3>

            <div className="room-upsell-upgrade-section">
              {/* <div className="room-upgrade-tag">
                <FaArrowUp /> {t("Recommended Upgrade")}
              </div> */}

              <div className="room-comparison">
                <div className="room-thumbnail-popup">
                  <img
                    src={
                      upgradedRoom?.RoomImages?.[0]?.cdnImageUrl ||
                      "https://ik.imagekit.io/hotelzifyprod/placeholder-upgrade-room.jpg"
                    }
                    alt={upgradedRoom.name}
                  />
                </div>

                <div className="room-upgrade-details">
                  <h4>{upgradedRoom.roomName}</h4>
                  <div className="room-upgrade-price">
                    <span className="upgrade-compare">
                      <span className="current-room">
                        {currentRoom.roomName}
                      </span>
                      <span className="arrow-icon">→</span>
                      <span className="upgraded-room">
                        {upgradedRoom.roomName}
                      </span>
                    </span>
                    <span className="price-difference">
                      {t("+")} {currency(currentRoom?.currency)}{" "}
                      {formattedPriceDiff}
                    </span>
                  </div>
                </div>
              </div>

              {/* Additional amenities section */}
              <div className="room-upsell-features-popup">
                <div className="amenities-header">
                  <h5>{t("Additional amenities you'll get")}</h5>
                  <div className="amenities-divider"></div>
                </div>

                <ul className="amenities-list">
                  {uniqueAmenities.length > 0 ? (
                    uniqueAmenities.map((feature, index) => (
                      <li key={index}>
                        <FaCheckCircle className="benefit-icon" />
                        <span>{feature}</span>
                      </li>
                    ))
                  ) : (
                    <li className="no-additional-amenities">
                      <span>
                        {t("Superior overall room quality and experience")}
                      </span>
                    </li>
                  )}
                </ul>

                {uniqueAmenities.length > 0 && (
                  <div className="upgrade-value-prop">
                    <p>
                      {t(
                        "Upgrade for an enhanced stay experience at a minimal additional cost"
                      )}
                    </p>
                  </div>
                )}
              </div>

              <div className="room-upsell-actions">
                <button className="upgrade-button" onClick={onUpgradeClick}>
                  {t("Upgrade Now")}
                </button>
                <button className="keep-selection-button" onClick={onDismiss}>
                  {t("No Thanks")}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RoomUpsellPopup;
